import { useCallback, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import SpriteSvg from "../components/common/svg";
import { Wrapper } from "../components/layout/wrapper";
import ArticleCarousel from "../components/carousel/article-carousel";
import { classNames, useAppConfig } from "@ct-react/core";
import { LocaleLink, useLocaleContext } from "@ct-react/locale";
import { ArticleType, buildSearchUrl } from "@shared/url";
import { CmsPageContent, extractCmsContent } from "@shared/graphql";
import { articleTypes } from "../i18n/sharable-defs";
import { SearchBar } from "../bundles/client-ui";
import RatioImage from "../components/medias/ratio-image";
import SeoMetas from "../components/layout/seo-metas";
import AboutSvg_1 from "../assets/componable-svgs/about-solid-01.svg";
import AboutSvg_2 from "../assets/componable-svgs/about-solid-02.svg";
import AboutSvg_3 from "../assets/componable-svgs/about-solid-03.svg";
import AboutImage from "../assets/img/location-verbier.jpg";
import "./home.scss";

const CMS_PAGE_GQL_DATA = gql`
  query HomePageCmsPage($templateId: UUID!) {
    cmsPages(templateId: $templateId) { data }
  }
`;

const transDefs = defineMessages({
  main_title: { id: "homepage.main-title", defaultMessage: "Trouvez les meilleurs chalets et appartements à Verbier." },
  section_1_title: { id: "homepage.section-1.title", defaultMessage: "Nos propriétés" },
  section_1_button: { id: "homepage.section-1.button", defaultMessage: "Découvrez toutes nos propriétés" },
  section_2_title: { id: "homepage.section-2.title", defaultMessage: "Planifiez votre séjour à Verbier" },
  section_2_text: { id: "homepage.section-2.text", defaultMessage: "Pour que vos vacances à Verbier soient les plus agréables possible, nous vous proposons une gamme complète de services adaptés à vos besoins. Choisissez ce qui vous convient le mieux, nous nous occupons du reste. Profitez pleinement de votre séjour, sans vous soucier de la logistique." },
  section_2_button: { id: "homepage.section-2.button", defaultMessage: "Découvrez plus d'informations utiles" },
  section_3_title: { id: "homepage.section-3.title", defaultMessage: "Ce que disent nos clients" },
  section_4_title: { id: "homepage.section-4.title", defaultMessage: "À propos" },
  section_4_text_1: { id: "homepage.section-4.text-1", defaultMessage: "Que vous recherchiez un studio confortable pour un court séjour ou un chalet de prestige pour vos vacances d'hiver, nous vous aidons à trouver le bien idéal qui répond à vos besoins. Grâce à notre connaissance approfondie de Verbier et à notre service personnalisé, vous profiterez d'un séjour sans tracas et bénéficierez d'informations locales." },
  section_4_list_1_title: { id: "homepage.section-4.list-1-title", defaultMessage: "Implanté à Verbier depuis 2008" },
  section_4_list_1_text: { id: "homepage.section-4.list-1-text", defaultMessage: "Nous connaissons presque tout sur Verbier. Cette expertise nous permet de vous conseiller avec précision et efficacité, que vous cherchiez à louer ou à acheter." },
  section_4_list_2_title: { id: "homepage.section-4.list-2-title", defaultMessage: "Une petite équipe, proche de ses clients" },
  section_4_list_2_text: { id: "homepage.section-4.list-2-text", defaultMessage: "Nous adaptons nos services à vos besoins spécifiques, sans aucune contrainte. Arrivées tardives, demandes personnalisées ou séjours uniques : tout est possible avec nous." },
  section_4_list_3_title: { id: "homepage.section-4.list-3-title", defaultMessage: "Un service efficace et convivial" },
  section_4_list_3_text: { id: "homepage.section-4.list-3-text", defaultMessage: "Nous allions professionnalisme et personnalisation. Nous sommes attentifs, accessibles et traitons vos demandes avec soin et bonne humeur." },
  section_5_title: { id: "homepage.section-5.title", defaultMessage: "Week-ends de ski, vacances en famille ou la maison de vos rêves à Verbier." },
  section_5_text: { id: "homepage.section-5.text", defaultMessage: "Nous sommes ici dans les Alpes pour vous aider à trouver le lieu de séjour idéal" },
  section_5_button: { id: "homepage.section-5.button", defaultMessage: "Contactez-nous" },
});

const carousel_option = {
  pagination: false,
  perPage:3,
  gap:"4rem",
  padding:"16px",
  breakpoints: {
    1200:{
      perPage:2,
      gap:"3rem",
    },
    576:{
      perPage:1,
      padding:"16px",
      gap:"1rem",
    }
  },
}

const HomePage = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { options: { cmsTemplates: { homepage: templateId } } } = useAppConfig();

  const [ carouselType, setCarouselType ] = useState<ArticleType>(ArticleType.RENTAL);
  const [ cmsContent, setCmsContent ] = useState<CmsPageContent | undefined>(undefined);

  const { data } = useQuery<{ cmsPages: any }>(CMS_PAGE_GQL_DATA, {
    ssr: true,
    variables: { templateId }
  });

  useEffect(() => {
    if (!data?.cmsPages || data.cmsPages.length === 0) return;
    setCmsContent(extractCmsContent(data.cmsPages[0].data, locale));
  }, [ data, locale ]);

  const typeButtonClasses = useCallback((type: ArticleType) =>
    classNames({ actif: type === carouselType }), [ carouselType ]);

  return(
    <>

      {(() => {
        if (!data?.cmsPages) return null;
        const seo = extractCmsContent(data!.cmsPages[0].data, locale)["seo-data"];
        return (
          <SeoMetas title={seo["seo-title"]}
                    description={seo["seo-description"]}
                    keywords={seo["seo-keywords"]?.split(",").map((k: string) => k.trim()) || []} />);
      })()}

      {/*-- Hero --*/}
      <section className="hero">
        <Wrapper.Centered>
          <div className="hero__title">
            <h1>{intl.formatMessage(transDefs.main_title)}</h1>
          </div>
          <div className="hero__icons">
            <a data-gloss={"+41 79 487 69 48"} href="tel:0041794876948">
              <SpriteSvg id="phone-line"/>
            </a>
            <a data-gloss={"info@verbierrentals.com"} href="mailto:info@verbierrentals.com">
              <SpriteSvg id="mail-line"/>
            </a>
            <a data-gloss={"Chem. des Vernes 5a, 1936 Verbier"} href="https://www.google.com/maps/place/Verbier+Rentals+S%C3%A0rl/@46.0952351,7.2256363,1507m/data=!3m2!1e3!4b1!4m6!3m5!1s0x478ece3357a5ef7b:0x27632d4a9acffc6c!8m2!3d46.0952314!4d7.2282112!16s%2Fg%2F1th1bmnt?entry=ttu&g_ep=EgoyMDI1MTAwMS4wIKXMDSoASAFQAw%3D%3D" target="_blank">
              <SpriteSvg id="localisation-line"/>
            </a>
          </div>
          <SearchBar />
        </Wrapper.Centered>
      </section>

      {/*-- Our properties --*/}
      <section className="section section-1">
        <Wrapper.Centered>
          <h2 className="centered-text">{intl.formatMessage(transDefs.section_1_title)}</h2>
          <div className="btns">
            {Object.values(ArticleType).map((type, index) => (
              <button key={index}
                      className={typeButtonClasses(type)}
                      onClick={() => setCarouselType(type)}>
                {intl.formatMessage(articleTypes[type])}
              </button>
            ))}
          </div>
          <ArticleCarousel type={carouselType} />
          <LocaleLink className="btn primary with-icon"
                      to={buildSearchUrl(carouselType)}>
            {intl.formatMessage(transDefs.section_1_button)}
            <SpriteSvg id="chevron-right" className="icon"/>
          </LocaleLink>
        </Wrapper.Centered>
      </section>

      {/*-- Planning your trip --*/}
      <section className="section section-2 bg-light">
        <Wrapper.Centered>
          <div className="centered-text">
            <h2>{intl.formatMessage(transDefs.section_2_title)}</h2>
            <p>{intl.formatMessage(transDefs.section_2_text)}</p>
          </div>
          <div className="services">
            {(!!cmsContent && !!cmsContent["hp-services-items"]) &&
              cmsContent["hp-services-items"].map((service: any, index: number) => (
                <div key={index}>
                  <img src={service["service-icon"].originalUrl} alt={service["service-icon"].alternativeText} />
                  <h3>{service["service-title"]}</h3>
                  <p>{service["service-intro"]}</p>
                </div>))
            }
          </div>
          <LocaleLink to="/useful-info" className="btn primary with-icon">
            {intl.formatMessage(transDefs.section_2_button)}
            <SpriteSvg id="chevron-right" className="icon"/>
          </LocaleLink>
        </Wrapper.Centered>
      </section>

      {/*-- What customers say --*/}
      <section className="section section-3">
        <Wrapper.Centered>
          <h2>{intl.formatMessage(transDefs.section_3_title)}</h2>
            <Splide hasTrack={ false } aria-label="Discover the carousel of testimony" options={carousel_option}>
              <div className="splide__arrows custom">
                <button className="splide__arrow splide__arrow--prev">
                  <SpriteSvg id="chevron-right"/>
                </button>
                <button className="splide__arrow splide__arrow--next">
                  <SpriteSvg id="chevron-right"/>
              </button>
              </div>
              <SplideTrack>
                {(!!cmsContent && !!cmsContent["hp-reviews-items"]) &&
                  cmsContent["hp-reviews-items"].map((review: any, index: number) => (
                    <SplideSlide key={index}>
                      <div  className="desc"
                            dangerouslySetInnerHTML={{ __html: review["review-content"] }} />
                      <hr/>
                      <div className="customer">
                        {review["review-customer"]}
                      </div>
                      <div className="complement">
                        {review["review-complement"]}
                      </div>
                    </SplideSlide>
                  ))
                }

              </SplideTrack>
            </Splide>
        </Wrapper.Centered>
      </section>

      {/*-- About us --*/}
      <section className="section section-4 bg-light">
        <Wrapper.Centered className="twoCol">
          <div className="image">
            <RatioImage className="preview-img" ratio="1/1" source={AboutImage} />
          </div>

          <div className="text">
            <h2>{intl.formatMessage(transDefs.section_4_title)}</h2>
            <p>{intl.formatMessage(transDefs.section_4_text_1)}</p>
            <ul>
              <li>
                  <div>
                    <AboutSvg_1/>
                  </div>
                  <div>
                    <p><strong>{intl.formatMessage(transDefs.section_4_list_1_title)}</strong></p>
                    <p>{intl.formatMessage(transDefs.section_4_list_1_text)}</p>
                  </div>
              </li>
              <li>
                  <div>
                    <AboutSvg_2/>
                  </div>
                  <div>
                    <p><strong>{intl.formatMessage(transDefs.section_4_list_2_title)}</strong></p>
                    <p>{intl.formatMessage(transDefs.section_4_list_2_text)}</p>
                  </div>

              </li>
              <li>
                  <div>
                    <AboutSvg_3/>
                  </div>
                  <div>
                    <p><strong>{intl.formatMessage(transDefs.section_4_list_3_title)}</strong></p>
                    <p>{intl.formatMessage(transDefs.section_4_list_3_text)}</p>
                  </div>

              </li>
            </ul>
          </div>
        </Wrapper.Centered>

        <Wrapper.Centered className="team">
          <Splide hasTrack={ false } aria-label="Discover the carousel's team of Verbier rentals" options={carousel_option}>
              <div className="splide__arrows custom">
                <button className="splide__arrow splide__arrow--prev">
                  <SpriteSvg id="chevron-right"/>
                </button>
                <button className="splide__arrow splide__arrow--next">
                  <SpriteSvg id="chevron-right"/>
              </button>
              </div>
            <SplideTrack>
              {(!!cmsContent && !!cmsContent["hp-team-members"]) &&
                cmsContent["hp-team-members"].map((member: any, index: number) => (
                  <SplideSlide key={index}>
                    <RatioImage className="preview-img" ratio="1/1" source={member["member-image"].assets} />
                    <h3>{member["member-name"]}</h3>
                    <div className="desc"
                         dangerouslySetInnerHTML={{ __html: member["member-description"] }} />
                    <div className="resp">
                      {member["member-responsabilities"]}
                    </div>
                  </SplideSlide>
                ))
              }
            </SplideTrack>
          </Splide>
        </Wrapper.Centered>
      </section>

      {/*-- Call to action --*/}
      <section className="section section-5">
        <Wrapper.Centered>
          <h2>{intl.formatMessage(transDefs.section_5_title)}</h2>
          <p>{intl.formatMessage(transDefs.section_5_text)}</p>
          <LocaleLink to="/contact" className="btn primary with-icon">
              {intl.formatMessage(transDefs.section_5_button)}
              <SpriteSvg id="chevron-right" className="icon"/>
          </LocaleLink>
        </Wrapper.Centered>
      </section>

    </>
  );

};

export default HomePage;
